import { useEffect, useState } from "react";
import Tealium from "@4tn/webx-analytics";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { PrimaryButton } from "@common/Buttons";
import Dropdown from "@common/Dropdown";
import { ItemsContainer } from "@pageContent/common/InfiniteCollection/InfiniteCollection";
import InfiniteCollectionItem from "@pageContent/common/InfiniteCollection/InfiniteCollectionItem";
import { CollectionSkeleton } from "@pageContent/common/Skeletons/InfiniteCollectionSkeleton";
import TextSkeleton from "@pageContent/common/Skeletons/TextSkeleton";
import { SHOW_MORE, noResultText, seasonCollectionTitle } from "@constants/consts";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import { useNoticeError } from "@utils/common/newRelic";
import useFetchLimit from "@utils/pageContent/useFetchLimit";
import { checkForSeasonAvailability, fetchEpisodesBySeasonId, fetchSeasons } from "@utils/video/videoApi";
import * as Styled from "./TvSeasonCollection.styled";

const initialFetchedItemsNumber = {
  xs: 16,
  sm: 18,
  md: 18,
  lg: 16,
};

async function paginatedEpisodesFetch(
  seasonId: string | undefined,
  limit: number,
  page: number
): Promise<{
  items: PageLink[];
  totalResults: number;
  nextPage: number | null;
}> {
  if (!seasonId) return { items: [], totalResults: 0, nextPage: null };
  const skip = page * limit;
  const { items, totalResults } = await fetchEpisodesBySeasonId(seasonId, skip, limit);
  if (typeof items === "undefined") throw new Error("Failed to retrieve episodes");

  return {
    items: items.map(({ videoGuid, episodeTitle, imageUrl, url, duration, dateLabel }) => ({
      id: videoGuid,
      imageUrl,
      title: episodeTitle,
      url,
      hasVideo: true,
      updatedAt: "",
      duration,
      dateLabel,
    })),
    totalResults: totalResults || 0,
    nextPage: items.length < limit ? null : page + 1,
  };
}

const TvSeasonItems: React.FC<{
  seasonId?: string;
  limit: number;
  activeId?: string;
}> = ({ seasonId, limit, activeId }) => {
  const seasonCollectionQueryKey = ["seasonCollection", seasonId, limit];

  const {
    data: links,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: seasonCollectionQueryKey,
    queryFn: ({ pageParam }) => paginatedEpisodesFetch(seasonId, limit, pageParam),
    initialPageParam: 0,
    select: (data) => data.pages.flatMap((pages) => pages.items),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

  useNoticeError(error, {
    queryKey: seasonCollectionQueryKey.join(","),
    seasonCollectionTitle,
    seasonId,
    activeId,
    limit,
  });

  const loadMore = () => {
    fetchNextPage();
    Tealium.link({
      event_category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
      event_name: TEALIUM_EVENT_NAME.CONTENT_LISTING_MORE,
      event_label: seasonCollectionTitle,
    });
  };

  if (seasonId && !isLoading && !links?.length) return <Styled.NoResultText>{noResultText}</Styled.NoResultText>;

  if (!seasonId || (isLoading && !links?.length))
    return (
      <ItemsContainer>
        <CollectionSkeleton title={seasonCollectionTitle} limit={limit} />
      </ItemsContainer>
    );

  return (
    <>
      <ItemsContainer>
        {links?.map((link, index) => (
          <InfiniteCollectionItem
            key={link.id}
            position={index + 1}
            sectionTitle={seasonCollectionTitle}
            link={link}
            isActive={link.id === activeId}
          />
        ))}
        {isFetchingNextPage && <CollectionSkeleton title={seasonCollectionTitle} limit={limit} />}
      </ItemsContainer>

      {hasNextPage && (
        <Styled.LoadMoreContainer>
          <PrimaryButton fullWidth disabled={isFetchingNextPage} onClick={loadMore}>
            {SHOW_MORE}
          </PrimaryButton>
        </Styled.LoadMoreContainer>
      )}
    </>
  );
};

interface TvSeasonCollectionProps {
  initialSeasons?: Array<DropdownOption> | null;
  seriesSlug?: string;
  activeId?: string;
  initialSeasonId?: string;
}

const TvSeasonCollection: React.FC<TvSeasonCollectionProps> = ({
  seriesSlug,
  initialSeasons,
  activeId,
  initialSeasonId,
}) => {
  const tvSeasonsQueryKey = ["tvSeasons", seriesSlug];

  const [seasonId, setSeasonId] = useState(initialSeasonId);
  const limit = useFetchLimit(initialFetchedItemsNumber);

  useEffect(() => {
    if (initialSeasonId) setSeasonId(initialSeasonId);
  }, [initialSeasonId]);

  const { data: seasons, error } = useQuery({
    queryKey: tvSeasonsQueryKey,
    queryFn: () => (seriesSlug ? fetchSeasons(seriesSlug).then(checkForSeasonAvailability) : null),
    enabled: !!seriesSlug,
    initialData: initialSeasons,
  });

  useNoticeError(error, { queryKey: tvSeasonsQueryKey.join(","), seriesSlug });

  const [firstSeason] = seasons || [];
  const activeSeasonId = seasonId || firstSeason?.value;

  return (
    <Styled.SeasonCollectionContainer>
      <Styled.TitleContainer>
        <Styled.Title>
          {!seasons?.length ? <TextSkeleton width={190} height={32} /> : seasonCollectionTitle}
        </Styled.Title>

        <Dropdown
          key={`${activeSeasonId}-${firstSeason?.value}`}
          defaultValue={activeSeasonId}
          options={seasons || []}
          callback={(value) => setSeasonId(value)}
          showActiveIcon
        />
      </Styled.TitleContainer>
      <TvSeasonItems seasonId={activeSeasonId} limit={limit} activeId={activeId} />
    </Styled.SeasonCollectionContainer>
  );
};

export default TvSeasonCollection;
